import { useEffect, useId, useRef } from 'react';
import type { RemovalCopy } from '../lib/removal.js';

/**
 * A modal confirmation for a destructive act.
 *
 * Two deliberate choices about which button is easy to hit:
 *
 *  - **Cancel takes focus on open**, not the destructive button. Someone who opens
 *    this by accident and hits Enter or Space should end up cancelling.
 *  - **Escape and a backdrop click both cancel.** Every cheap, reflexive way out of
 *    a dialog leads to the safe outcome.
 *
 * The wording comes from `lib/removal.ts`, where it is derived from the bid and
 * tested, so a dialog cannot claim an act is permanent when it is not.
 */
export function ConfirmDialog({
  copy,
  busy,
  onConfirm,
  onCancel,
}: {
  copy: RemovalCopy;
  busy: boolean;
  onConfirm(): void;
  onCancel(): void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    cancelRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);

    // Stop the list behind the dialog scrolling under it.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onCancel]);

  return (
    <div
      className="backdrop"
      // A click on the backdrop itself cancels; a click inside the dialog must not.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={bodyId}>
        <h3 id={titleId}>{copy.title}</h3>
        <p id={bodyId}>{copy.body}</p>

        <div className="dialog-actions">
          <button className="btn-cancel" ref={cancelRef} disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          <button className="btn-danger" disabled={busy} onClick={onConfirm}>
            {busy ? 'Working…' : copy.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
